import React, { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { FaBook, FaFileExcel, FaFilter, FaHistory } from "react-icons/fa";
import * as XLSX from "xlsx";
import { obtenerLibroDiarioPorEmpresa, obtenerLibroMayorPorEmpresa } from "../../../services/contabilidad/asientosService";
import { periodoActual, toNum } from "../utils";

const BTN = { background: "linear-gradient(135deg, #3a3a3a 0%, #606060 100%)", border: "none", color: "white" };

const LibrosContables = ({ usuario }) => {
  const rucEmpresa = usuario?.ruc || "";
  const [periodo, setPeriodo] = useState(periodoActual());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [diario, setDiario] = useState([]);

  const cargarDatos = useCallback(async () => {
    if (!rucEmpresa) return;
    try {
      setLoading(true); setError("");
      const [diarioData] = await Promise.all([obtenerLibroDiarioPorEmpresa(rucEmpresa), obtenerLibroMayorPorEmpresa(rucEmpresa)]);
      setDiario(diarioData.filter(a => !periodo || String(a.fecha).startsWith(periodo)));
    } catch (e) { setError("No se pudieron cargar los libros contables."); }
    finally { setLoading(false); }
  }, [rucEmpresa, periodo]);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  const exportarExcel = () => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(diario.map(a => ({ Fecha: a.fecha, Glosa: a.glosa, Debe: toNum(a.total_debe).toFixed(2), Haber: toNum(a.total_haber).toFixed(2) }))), "Libro_Diario");
    XLSX.writeFile(wb, `cont_libros_${rucEmpresa}_${periodo}.xlsx`);
  };

  const exportarTxtPle = (tipo) => {
    const nombre = `LE${rucEmpresa}${periodo.replace("-", "")}000${tipo === "DIARIO" ? "0501" : "0601"}001111.txt`;
    const contenido = diario.map(a => `${a.fecha}|${a.glosa}|${toNum(a.total_debe).toFixed(2)}|${toNum(a.total_haber).toFixed(2)}`).join("\n");
    const url = URL.createObjectURL(new Blob([contenido], { type: "text/plain;charset=utf-8" }));
    const link = document.createElement("a"); link.href = url; link.download = nombre; link.click(); URL.revokeObjectURL(url);
  };

  if (!rucEmpresa) return <Alert variant="warning" className="border-0 shadow-sm">No se encontró RUC de empresa.</Alert>;

  return (
    <div>
      {error && <Alert variant="danger" onClose={() => setError("")} dismissible className="border-0 shadow-sm">{error}</Alert>}

      {/* Barra de acción */}
      <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
        <Card.Body className="p-4">
          <Row className="align-items-center">
            <Col md={1} className="text-center">
              <div className="rounded-3 p-3 d-inline-block" style={BTN}><FaBook size={20} /></div>
            </Col>
            <Col md={5}>
              <h5 className="mb-1 fw-semibold">Libros Electrónicos (PLE)</h5>
              <p className="text-muted mb-0 small">Visualice y exporte el Libro Diario oficial para SUNAT.</p>
            </Col>
            <Col md={3}>
              <div className="input-group shadow-sm" style={{ borderRadius: 10, overflow: "hidden" }}>
                <span className="input-group-text border-0 bg-white"><FaFilter className="text-muted" style={{ fontSize: "0.8rem" }} /></span>
                <Form.Control type="month" className="border-0 fw-bold" value={periodo} onChange={(e) => setPeriodo(e.target.value)} />
              </div>
            </Col>
            <Col md={3} className="text-end d-flex gap-2 justify-content-end">
              <Button variant="outline-success" className="rounded-pill px-3 border-2 fw-bold" onClick={exportarExcel} disabled={loading}>
                <FaFileExcel className="me-1" /> Excel
              </Button>
              <Button size="sm" className="rounded-pill px-3 border-0" style={BTN} onClick={() => exportarTxtPle("DIARIO")} disabled={loading}>PLE 5.1</Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* KPIs */}
      <Row className="mb-4 g-3">
        <Col md={4}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
            <Card.Body className="text-center py-4" style={{ background: "linear-gradient(135deg, #3a3a3a 0%, #606060 100%)", color: "white", borderRadius: "15px" }}>
              <FaHistory size="2rem" className="mb-2" />
              <h4 className="fw-bold mb-0">{diario.length}</h4>
              <p className="mb-0 small opacity-75">Asientos del periodo</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
            <Card.Body className="text-center py-4" style={{ background: "linear-gradient(135deg, #059669 0%, #10b981 100%)", color: "white", borderRadius: "15px" }}>
              <FaBook size="2rem" className="mb-2" />
              <h4 className="fw-bold mb-0">S/ {diario.reduce((a, x) => a + toNum(x.total_debe), 0).toFixed(2)}</h4>
              <p className="mb-0 small opacity-75">Total Debe</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
            <Card.Body className="text-center py-4" style={{ background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)", color: "white", borderRadius: "15px" }}>
              <FaBook size="2rem" className="mb-2" />
              <h4 className="fw-bold mb-0">S/ {diario.reduce((a, x) => a + toNum(x.total_haber), 0).toFixed(2)}</h4>
              <p className="mb-0 small opacity-75">Total Haber</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabla */}
      <Card className="border-0 shadow-sm" style={{ borderRadius: "15px", overflow: "hidden" }}>
        <div className="table-responsive" style={{ maxHeight: 400 }}>
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="secondary" /><p className="mt-3 text-muted">Cargando libros...</p></div>
          ) : diario.length === 0 ? (
            <div className="text-center py-5">
              <FaBook size="4rem" className="text-muted opacity-25 mb-3" />
              <h5 className="text-muted fw-semibold">No hay movimientos para este periodo</h5>
            </div>
          ) : (
            <Table hover className="align-middle bg-white mb-0" style={{ fontSize: "0.95rem" }}>
              <thead style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                <tr>
                  <th className="py-3 px-4 text-secondary fw-semibold">Fecha</th>
                  <th className="py-3 px-4 text-secondary fw-semibold">Glosa</th>
                  <th className="py-3 px-4 text-secondary fw-semibold text-end">Debe</th>
                  <th className="py-3 px-4 text-secondary fw-semibold text-end">Haber</th>
                  <th className="py-3 px-4 text-secondary fw-semibold text-center">Origen</th>
                </tr>
              </thead>
              <tbody>
                {diario.slice(0, 100).map((a) => (
                  <tr key={a.id} style={{ borderBottom: "1px solid #f1f3f4" }}>
                    <td className="px-4 py-3 small fw-medium text-dark">{a.fecha || "—"}</td>
                    <td className="px-4 py-3 small" style={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.glosa || "—"}</td>
                    <td className="px-4 py-3 text-end fw-bold">{toNum(a.total_debe).toFixed(2)}</td>
                    <td className="px-4 py-3 text-end fw-bold">{toNum(a.total_haber).toFixed(2)}</td>
                    <td className="px-4 py-3 text-center">
                      <Badge style={{ background: "rgba(107,114,128,0.1)", color: "#374151", border: "1px solid rgba(107,114,128,0.25)", borderRadius: "8px" }} className="px-2 py-1">
                        {a.origen || "GENÉRICO"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </Card>
    </div>
  );
};

export default LibrosContables;
